import React, { useEffect, useRef, useState } from 'react'
import styles from './LocalPopup.module.css'
import { BasicStyledComponent } from 'shared-definitions/types'
import clsx from 'clsx'

interface LocalPopupProps extends BasicStyledComponent {
  onBackgroundClick?: () => void
  open: boolean
  /**
   * Opt-in: children are in the DOM only while the popup is open or fading out. The container
   * itself stays mounted (at opacity 0), so opening still animates. Without it, children stay
   * mounted while closed — invisible, but present for any script that scans the page.
   */
  unmountWhenClosed?: boolean
  /** With `unmountWhenClosed`: the fade-out finished and the children are gone. */
  onExited?: () => void
}

// Slack past the computed duration before the fallback timer fires: `transitionend` is the
// normal path, the timer covers a transition that never started (closed again within the
// same frame it opened) or was interrupted.
const FADE_OUT_SLACK_MS = 50

function parseTimeMs(value: string): number {
  const match = /^([\d.]+)(ms|s)$/.exec(value.trim())
  if (!match) {
    return 0
  }
  const n = Number(match[1])
  return match[2] === 's' ? n * 1000 : n
}

/**
 * How long the element's opacity transition takes (duration + delay), from its computed style;
 * 0 when it has none (`transition: none` under prefers-reduced-motion, CSS not loaded). The
 * value LocalPopup's own CSS gives today is 0.2s (`@mixin transition opacity`,
 * packages/shared-definitions/postcss-make-config.js `transitions`) — read, not hard-coded, so
 * a caller's override is honoured.
 */
export function opacityTransitionMs(
  style: Pick<CSSStyleDeclaration, 'transitionProperty' | 'transitionDuration' | 'transitionDelay'>
): number {
  const properties = (style.transitionProperty || '').split(',').map(p => p.trim())
  const index = properties.findIndex(p => p === 'opacity' || p === 'all')
  if (index < 0) {
    return 0
  }
  // CSS repeats the shorter lists to match the property list.
  const durations = (style.transitionDuration || '0s').split(',')
  const delays = (style.transitionDelay || '0s').split(',')
  return (
    parseTimeMs(durations[index % durations.length] ?? '0s') +
    parseTimeMs(delays[index % delays.length] ?? '0s')
  )
}

type TransitionEndListener = (e: { target: unknown; propertyName: string }) => void

/**
 * Calls `done` once `el`'s opacity fade-out has ended: its own `transitionend` for `opacity`
 * (events bubbling up from a child's transition are ignored), or a fallback timer, or at once
 * when there is no transition. Returns a cancel — the popup re-opened mid-fade.
 */
export function afterFadeOut(
  el: {
    addEventListener: (type: 'transitionend', listener: TransitionEndListener) => void
    removeEventListener: (type: 'transitionend', listener: TransitionEndListener) => void
  },
  durationMs: number,
  done: () => void
): () => void {
  if (durationMs <= 0) {
    done()
    return () => undefined
  }
  let pending = true
  const onEnd: TransitionEndListener = e => {
    if (e.target === el && e.propertyName === 'opacity') {
      finish()
    }
  }
  const timer = setTimeout(() => finish(), durationMs + FADE_OUT_SLACK_MS)
  function stop(): void {
    pending = false
    clearTimeout(timer)
    el.removeEventListener('transitionend', onEnd)
  }
  function finish(): void {
    if (pending) {
      stop()
      done()
    }
  }
  el.addEventListener('transitionend', onEnd)
  return stop
}

function fadeOutMs(el: HTMLElement): number {
  // Reduced motion: the content goes now, whatever the CSS says (a caller should also drop its
  // transition there, or an empty backdrop fades out alone).
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return 0
  }
  return opacityTransitionMs(window.getComputedStyle(el))
}

const LocalPopup: React.FC<React.PropsWithChildren<LocalPopupProps>> = ({
  className,
  children,
  open,
  onBackgroundClick,
  unmountWhenClosed = false,
  onExited,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  // Still fading out. Children render on `open` itself, never a commit later: a caller's effect
  // keyed on its open flag must find its refs attached in the same commit.
  const [present, setPresent] = useState(open)
  const onExitedRef = useRef(onExited)
  onExitedRef.current = onExited
  // The fade-out shows what the reader saw while it was open: a close often lands in the same
  // batch as a state change (confirm → sign-in mode, "Signing you in…" → the form, the name
  // step ending), and rendering the live children would reshape the dialog as it fades.
  const lastOpenChildren = useRef(children)
  if (open) {
    lastOpenChildren.current = children
  }

  useEffect(() => {
    if (!unmountWhenClosed) {
      return
    }
    if (open) {
      setPresent(true)
      return
    }
    if (!present) {
      return
    }
    const exit = (): void => {
      setPresent(false)
      onExitedRef.current?.()
    }
    const el = ref.current
    if (!el) {
      exit()
      return
    }
    // Re-opening runs this effect's cleanup first, which cancels the pending unmount.
    return afterFadeOut(el, fadeOutMs(el), exit)
  }, [open, present, unmountWhenClosed])

  let rendered: React.ReactNode = null
  if (!unmountWhenClosed || open) {
    rendered = children
  } else if (present) {
    rendered = lastOpenChildren.current
  }

  // The fade-in needs no frame dance: the container is mounted (at opacity 0) from the start,
  // so dropping `.closed` transitions it even though the children mount in the same commit.
  return (
    <div
      ref={ref}
      className={clsx(styles.container, className, {
        [styles.closed]: !open,
      })}
      data-popup
      // Hidden from assistive tech only while closed: the sign-in, name and device dialogs render
      // inside this container, and an always-on aria-hidden made every one of them invisible.
      aria-hidden={open ? undefined : true}
      role="presentation"
      onClick={(e: { target: EventTarget }) => {
        const target = e.target as HTMLElement
        if (target.dataset['popup']) {
          onBackgroundClick?.()
        }
      }}
    >
      {rendered}
    </div>
  )
}

export default LocalPopup
